/**
 * Features API Client
 */

import type {
  IFeature,
  TFeatureId,
  FeatureStatus,
  ICreateFeatureRequest,
  IUpdateFeatureRequest,
} from "@/shared/types/feature";
import { createFeatureId, FEATURE_STATUSES } from "@/shared/types/feature";
import { assertEntityId } from "@/shared/types/validation";
import { httpClient } from "./client";

export interface IFeaturesApiClient {
  listByProject(projectId: string, includeArchived?: boolean): Promise<IFeature[]>;
  create(projectId: string, request: ICreateFeatureRequest): Promise<IFeature>;
  update(
    projectId: string,
    featureId: TFeatureId,
    request: IUpdateFeatureRequest
  ): Promise<IFeature>;
  remove(projectId: string, featureId: TFeatureId): Promise<void>;
}

type IFeatureResponse = {
  readonly id: string | number;
  readonly projectId: string | number;
  readonly parentFeatureId?: string | number;
  readonly name: string;
  readonly description?: string;
  readonly status: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly isDeleted: boolean;
  readonly deletedAt?: string;
  readonly lastSeenAt: string;
};

function validateFeatureStatus(value: string): FeatureStatus {
  if (FEATURE_STATUSES.includes(value as FeatureStatus)) {
    return value as FeatureStatus;
  }
  throw new Error(`Invalid feature status: ${value}`);
}

function normalizeFeature(data: IFeatureResponse): IFeature {
  return {
    // Форму идентификатора клиент не выясняет: число и его строковая запись —
    // один и тот же идентификатор (dto-conventions-R11).
    id: createFeatureId(assertEntityId(data.id, "feature.id")),
    projectId: assertEntityId(data.projectId, "feature.projectId"),
    parentFeatureId:
      data.parentFeatureId !== undefined
        ? createFeatureId(
            assertEntityId(data.parentFeatureId, "feature.parentFeatureId")
          )
        : undefined,
    name: data.name,
    description: data.description,
    status: validateFeatureStatus(data.status),
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    isDeleted: data.isDeleted,
    deletedAt: data.deletedAt,
    lastSeenAt: data.lastSeenAt,
  };
}

export class FeaturesApiClient implements IFeaturesApiClient {
  /**
   * Перечень фич проекта (business-model-features-R01). Архивные добавляются
   * только по просьбе поверхности управления фичами (feature-lifecycle-R28) —
   * селектор фичи их не получает.
   */
  async listByProject(
    projectId: string,
    includeArchived = false
  ): Promise<IFeature[]> {
    const response = await httpClient.get<IFeatureResponse[]>(
      `/projects/${projectId}/features`,
      includeArchived ? { params: { includeArchived: true } } : undefined
    );
    return response.data.map(normalizeFeature);
  }

  /**
   * Отказ продукта наверх уходит **как есть**: признак причины и параметры
   * нужны, чтобы показать пользователю, что именно не так — занятое название
   * от пустого отличимо только по ним (api-errors-R06, R08). Подмена отказа
   * собственной ошибкой признак теряла.
   */
  async create(
    projectId: string,
    request: ICreateFeatureRequest
  ): Promise<IFeature> {
    const response = await httpClient.post<IFeatureResponse>(
      `/projects/${projectId}/features`,
      request
    );
    return normalizeFeature(response.data);
  }

  /**
   * Изменение частично: название, описание, статус — любое подмножество
   * (business-model-features-R07). Фича адресуется в границах проекта: чужая
   * неотличима от несуществующей (rbac-R25).
   */
  async update(
    projectId: string,
    featureId: TFeatureId,
    request: IUpdateFeatureRequest
  ): Promise<IFeature> {
    const response = await httpClient.put<IFeatureResponse>(
      `/projects/${projectId}/features/${featureId}`,
      request
    );
    return normalizeFeature(response.data);
  }

  /**
   * Удаление фичи (feature-lifecycle-R11–R17). Удержания и каскад — свойство
   * продукта: клиент их не проверяет и не обходит (R24), а показывает отказ.
   */
  async remove(projectId: string, featureId: TFeatureId): Promise<void> {
    await httpClient.delete(`/projects/${projectId}/features/${featureId}`);
  }
}

// Singleton instance
let featuresApiClient: FeaturesApiClient | null = null;

export function getFeaturesApiClient(): FeaturesApiClient {
  featuresApiClient ??= new FeaturesApiClient();
  return featuresApiClient;
}
